import React, { useEffect } from 'react';
import { get } from 'lodash';
import DatePicker, { DatePickerProps } from '@lendi-ui/date-picker';

import { formatDate } from '@funnels/utils';
import { useFormValues } from '@funnels/hooks';

import { CommonFieldProps } from '../_common/types';

function extractDateValues(date?: string) {
  if (!date) {
    return ['', '', ''];
  }

  const dateArr = date.split('-');
  const [year = ''] = dateArr;
  let [, month = '', day = ''] = dateArr;

  if (month === '00') {
    month = '';
  } else if (month?.charAt(0) === '0') {
    month = `${parseInt(month, 10)}`;
  }

  if (day.indexOf('T')) {
    [day] = day.split('T');
  }

  if (day === '00') {
    day = '';
  } else if (day?.charAt(0) === '0') {
    day = `${parseInt(day, 10)}`;
  }

  return [year, month, day];
}

export interface DatePickerInputProps
  extends CommonFieldProps,
    Omit<DatePickerProps, 'monthValue' | 'monthOnChange' | 'yearValue' | 'yearOnChange'> {
  value?: string;
}

function DatePickerInput(props: DatePickerInputProps) {
  const { name, value, hasDayField, ...rest } = props;
  const [formValues, setFormValue] = useFormValues();
  const [initialYear, initialMonth, initialDay] = extractDateValues(value);
  const [yearValue, setYearValue] = React.useState(initialYear);
  const [monthValue, setMonthValue] = React.useState(initialMonth);
  const [dayValue, setDayValue] = React.useState(!hasDayField ? '01' : initialDay);

  function handleYearOnChange(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    setYearValue(e.target.value);
    handleDateChange(e.target.value);
  }

  function handleMonthOnChange(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();

    if (parseInt(e.target.value, 10) > 12) {
      return;
    }

    setMonthValue(e.target.value);
    handleDateChange(undefined, e.target.value);
  }

  function handleDayOnChange(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();

    if (parseInt(e.target.value, 10) > 31) {
      return;
    }

    setDayValue(e.target.value);
    handleDateChange(undefined, undefined, e.target.value);
  }

  function handleDateChange(years?: string, months?: string, days?: string) {
    // reset date string to undefined when there has no year and month
    if (!years && !months && (!yearValue || !monthValue)) {
      setFormValue(name, undefined);
      return;
    }

    const formattedDate = formatDate(years ?? yearValue, months ?? monthValue, days ?? dayValue);

    setFormValue(name, formattedDate);
  }

  useEffect(() => {
    const formattedDate = get(formValues, name, '');
    const dateValues = extractDateValues(formattedDate);
    const [yearValue, monthValue, dayValue] = dateValues;

    setYearValue(yearValue);
    setMonthValue(monthValue);

    if (hasDayField) {
      setDayValue(dayValue);
    }
  }, [formValues]);

  return (
    <DatePicker
      size="md"
      yearValue={yearValue}
      monthValue={monthValue}
      dayValue={dayValue}
      yearOnChange={handleYearOnChange}
      monthOnChange={handleMonthOnChange}
      dayOnChange={handleDayOnChange}
      hasDayField={hasDayField}
      {...rest}
    />
  );
}

export default DatePickerInput;
